import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import {
  MONTH_NAMES, fetchGoalTypes, goalTypeLabel,
  fmtBRL, apiErrorMessage, fetchCompanies, preferredCompanyId,
} from '../utils/clinicalUtils';
import './clinical.css';

/**
 * ClinicalGoalsPage — CRUD de metas (com a validação RN-12 refletida na UI)
 * e de contadores de produção. Sem reload após mutação.
 */

const EMPTY_GOAL = {
  goal_type: '',
  period_type: 'weekly',
  week_number: '1',
  target_value: '',
};

const EMPTY_COUNTER = {
  counter_type: '',
  week_number: '1',
  quantity: '',
};

const EMPTY_CATALOG_TYPE = {
  key: '',
  label: '',
  applies_to_goals: false,
  applies_to_counters: false,
  active: true,
};

// Normaliza o texto digitado em `key` pro formato de slug exigido pelo
// backend: minúsculas, sem acento, sem espaço (vira "_"), só [a-z0-9_].
function normalizeGoalTypeKey(raw) {
  return String(raw || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

function ClinicalGoalsPage() {
  const now = new Date();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [goals, setGoals] = useState([]);
  const [counters, setCounters] = useState([]);
  const [goalTypeCatalog, setGoalTypeCatalog] = useState([]);
  const [goalTypesLoaded, setGoalTypesLoaded] = useState(false);
  const [goalForm, setGoalForm] = useState(EMPTY_GOAL);
  const [counterForm, setCounterForm] = useState(EMPTY_COUNTER);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [editingCounterId, setEditingCounterId] = useState(null);

  const [catalogForm, setCatalogForm] = useState(EMPTY_CATALOG_TYPE);
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [catalogOpen, setCatalogOpen] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchCompanies()
      .then((list) => {
        setCompanies(list);
        if (list.length) setCompanyId(preferredCompanyId(list));
      })
      .catch(() => setError('Falha ao carregar as unidades.'));
  }, []);

  // Catálogo de tipos de meta/contador da unidade — só depende de companyId
  // (não de mês/ano, diferente de goals/counters).
  useEffect(() => {
    if (!companyId) {
      setGoalTypeCatalog([]);
      setGoalTypesLoaded(false);
      return;
    }
    setGoalTypesLoaded(false);
    // o catálogo é por empresa — um goal_type/counter_type escolhido pra
    // unidade anterior pode nem existir na nova, então zera pra deixar o
    // efeito abaixo repreencher com o primeiro tipo disponível da unidade atual.
    setGoalForm((f) => ({ ...f, goal_type: '' }));
    setCounterForm((f) => ({ ...f, counter_type: '' }));
    // idem pro form de cadastro de tipo — evita editar um tipo da unidade
    // anterior ao trocar de empresa.
    setCatalogForm(EMPTY_CATALOG_TYPE);
    setEditingTypeId(null);
    // active: null traz ativos + inativos — a tela de gerenciamento precisa
    // listar tipos desativados também, senão não há como reativá-los (os
    // dropdowns de meta/contador abaixo já filtram só os ativos por conta
    // própria, ver goalTypeOptions/counterTypeOptions).
    fetchGoalTypes({ companyId, active: null })
      .then((list) => setGoalTypeCatalog(list))
      .catch(() => setError('Falha ao carregar os tipos de meta/contador cadastrados.'))
      .finally(() => setGoalTypesLoaded(true));
  }, [companyId]);

  // Abre a seção de catálogo automaticamente na primeira vez que ficar claro
  // que a unidade selecionada não tem nenhum tipo cadastrado ainda (fluxo de
  // onboarding — sem isso a empresa fica travada nos formulários abaixo, que
  // ficam vazios). Não força fechar depois que a empresa cadastra o
  // primeiro tipo nem reabre à toa quando o catálogo já está populado — só
  // reage à transição "carregou e está vazio".
  useEffect(() => {
    if (goalTypesLoaded && goalTypeCatalog.length === 0) setCatalogOpen(true);
  }, [goalTypesLoaded, goalTypeCatalog.length]);

  // 'google' fora da lista de metas: a meta dele não é cadastrada — é
  // dinâmica, sempre igual à quantidade de orçamentos novos (budgets_count)
  // da própria semana (100% = 1 avaliação do Google por orçamento). Uma
  // meta cadastrada aqui seria ignorada pelo dashboard, então nem
  // oferecemos a opção — mesmo que o catálogo da empresa tenha esse tipo.
  const goalTypeOptions = useMemo(
    () => goalTypeCatalog.filter((t) => t.active && t.applies_to_goals && t.key !== 'google'),
    [goalTypeCatalog]
  );
  const counterTypeOptions = useMemo(
    () => goalTypeCatalog.filter((t) => t.active && t.applies_to_counters),
    [goalTypeCatalog]
  );

  // Assim que o catálogo carrega, preenche o form com o primeiro tipo
  // disponível (evita mandar goal_type/counter_type vazio pro backend).
  useEffect(() => {
    setGoalForm((f) => (f.goal_type ? f : { ...f, goal_type: goalTypeOptions[0]?.key || '' }));
  }, [goalTypeOptions]);

  useEffect(() => {
    setCounterForm((f) => (f.counter_type ? f : { ...f, counter_type: counterTypeOptions[0]?.key || '' }));
  }, [counterTypeOptions]);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    try {
      const params = { company_id: companyId, reference_year: year, reference_month: month };
      const [goalsRes, countersRes] = await Promise.all([
        api.get('/clinical-results/goals', { params }),
        api.get('/clinical-results/counters', { params }),
      ]);
      setGoals(goalsRes.data || []);
      setCounters(countersRes.data || []);
    } catch (err) {
      setError(apiErrorMessage(err, 'Falha ao carregar metas e contadores.'));
    }
  }, [companyId, year, month]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // RN-12 refletida na UI: soma das semanais x mensal, por tipo (só ativas)
  const goalCheck = (type) => {
    const active = goals.filter((g) => g.goal_type === type && g.active);
    const monthly = active.find((g) => g.period_type === 'monthly');
    const weeklySum = active
      .filter((g) => g.period_type === 'weekly')
      .reduce((acc, g) => acc + Number(g.target_value), 0);
    if (!monthly || weeklySum === 0) return null;
    const ok = Math.abs(weeklySum - Number(monthly.target_value)) <= 0.01;
    return { ok, weeklySum, monthlyTarget: Number(monthly.target_value) };
  };

  // ------------------------------------------------------------- METAS

  const handleGoalChange = (e) => {
    const { name, value } = e.target;
    setGoalForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGoalSubmit = async () => {
    setError('');
    setSuccess('');
    try {
      const payload = {
        company_id: companyId,
        goal_type: goalForm.goal_type,
        period_type: goalForm.period_type,
        reference_year: year,
        reference_month: month,
        week_number: goalForm.period_type === 'weekly' ? Number(goalForm.week_number) : null,
        target_value: Number(goalForm.target_value),
      };

      if (editingGoalId) {
        const { data } = await api.put(`/clinical-results/goals/${editingGoalId}`, payload);
        setGoals((prev) => prev.map((g) => (g.id === data.id ? data : g)));
        setSuccess('Meta atualizada.');
      } else {
        const { data } = await api.post('/clinical-results/goals', payload);
        setGoals((prev) => [...prev, data]);
        setSuccess('Meta criada.');
      }
      setGoalForm(EMPTY_GOAL);
      setEditingGoalId(null);
    } catch (err) {
      setError(apiErrorMessage(err)); // inclui a mensagem da RN-12/RN-14
    }
  };

  const handleGoalEdit = (goal) => {
    setEditingGoalId(goal.id);
    setGoalForm({
      goal_type: goal.goal_type,
      period_type: goal.period_type,
      week_number: goal.week_number ? String(goal.week_number) : '1',
      target_value: String(goal.target_value),
    });
  };

  const handleGoalDelete = async (goal) => {
    if (!window.confirm('Desativar esta meta?')) return;
    setError('');
    try {
      await api.delete(`/clinical-results/goals/${goal.id}`);
      setGoals((prev) => prev.map((g) => (g.id === goal.id ? { ...g, active: false } : g)));
      setSuccess('Meta desativada.');
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  // -------------------------------------------------------- CONTADORES

  const handleCounterChange = (e) => {
    const { name, value } = e.target;
    setCounterForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCounterSubmit = async () => {
    setError('');
    setSuccess('');
    try {
      const payload = {
        company_id: companyId,
        reference_year: year,
        reference_month: month,
        week_number: Number(counterForm.week_number),
        counter_type: counterForm.counter_type,
        quantity: Number(counterForm.quantity),
      };

      if (editingCounterId) {
        const { data } = await api.put(`/clinical-results/counters/${editingCounterId}`, payload);
        setCounters((prev) => prev.map((c) => (c.id === data.id ? data : c)));
        setSuccess('Contador atualizado.');
      } else {
        const { data } = await api.post('/clinical-results/counters', payload);
        setCounters((prev) => [...prev, data]);
        setSuccess('Contador criado.');
      }
      setCounterForm(EMPTY_COUNTER);
      setEditingCounterId(null);
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  const handleCounterEdit = (counter) => {
    setEditingCounterId(counter.id);
    setCounterForm({
      counter_type: counter.counter_type,
      week_number: String(counter.week_number),
      quantity: String(counter.quantity),
    });
  };

  const handleCounterDelete = async (counter) => {
    if (!window.confirm('Excluir este contador?')) return;
    setError('');
    try {
      await api.delete(`/clinical-results/counters/${counter.id}`);
      setCounters((prev) => prev.filter((c) => c.id !== counter.id));
      setSuccess('Contador excluído.');
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  // ------------------------------------------------ CATÁLOGO DE TIPOS

  const reloadGoalTypeCatalog = useCallback(async () => {
    const list = await fetchGoalTypes({ companyId, active: null });
    setGoalTypeCatalog(list);
  }, [companyId]);

  const handleCatalogChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'key') {
      setCatalogForm((prev) => ({ ...prev, key: normalizeGoalTypeKey(value) }));
      return;
    }
    setCatalogForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const catalogFormValid = catalogForm.key.trim() !== ''
    && catalogForm.label.trim() !== ''
    && (catalogForm.applies_to_goals || catalogForm.applies_to_counters);

  const handleCatalogSubmit = async () => {
    setError('');
    setSuccess('');
    try {
      const payload = {
        key: catalogForm.key,
        label: catalogForm.label.trim(),
        applies_to_goals: catalogForm.applies_to_goals,
        applies_to_counters: catalogForm.applies_to_counters,
        active: catalogForm.active,
      };

      if (editingTypeId) {
        await api.put(`/clinical-results/goal-types/${editingTypeId}`, payload);
        setSuccess('Tipo atualizado.');
      } else {
        await api.post('/clinical-results/goal-types', payload);
        setSuccess('Tipo criado.');
      }
      await reloadGoalTypeCatalog();
      setCatalogForm(EMPTY_CATALOG_TYPE);
      setEditingTypeId(null);
    } catch (err) {
      setError(apiErrorMessage(err)); // inclui 409 de key duplicada / 400 de validação
    }
  };

  const handleCatalogEdit = (t) => {
    setEditingTypeId(t.id);
    setCatalogForm({
      key: t.key,
      label: t.label,
      applies_to_goals: t.applies_to_goals,
      applies_to_counters: t.applies_to_counters,
      active: t.active,
    });
    setError('');
    setSuccess('');
  };

  const handleCatalogCancelEdit = () => {
    setEditingTypeId(null);
    setCatalogForm(EMPTY_CATALOG_TYPE);
  };

  const handleCatalogDelete = async (t) => {
    if (!window.confirm(`Remover o tipo "${t.label}"?`)) return;
    setError('');
    setSuccess('');
    try {
      const { data } = await api.delete(`/clinical-results/goal-types/${t.id}`);
      setSuccess(
        data && data.deactivated
          ? 'Tipo desativado (havia uso em metas/contadores existentes).'
          : 'Tipo removido.'
      );
      await reloadGoalTypeCatalog();
      if (editingTypeId === t.id) handleCatalogCancelEdit();
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  const revenueCheck = goalCheck('revenue');

  return (
    <div className="clinical-page">
      <h1>Metas e Contadores — Resultados da Unidade</h1>
      <p className="clinical-subtitle">A soma das metas semanais deve igualar a meta mensal do mesmo tipo.</p>

      {error && <div className="clinical-error">{error}</div>}
      {success && <div className="clinical-success">{success}</div>}

      <div className="clinical-filters">
        <label>
          Unidade
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label>
          Mês
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTH_NAMES.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
          </select>
        </label>
        <label>
          Ano
          <input type="number" min="2020" max="2100" value={year} onChange={(e) => setYear(Number(e.target.value))} />
        </label>
      </div>

      <details
        className="clinical-catalog"
        open={catalogOpen}
        onToggle={(e) => setCatalogOpen(e.currentTarget.open)}
      >
        <summary className="clinical-catalog-summary">
          Gerenciar tipos de meta/contador desta unidade
        </summary>

        <p className="clinical-subtitle">
          Cadastre aqui os tipos que vão aparecer nos formulários de meta e contador abaixo
          (ex.: Receita, Orçamentos, Facetas). Cada unidade tem seu próprio catálogo.
        </p>

        <div className="clinical-form">
          <label className="form-full">
            Identificador (key)
            <input
              type="text"
              name="key"
              value={catalogForm.key}
              onChange={handleCatalogChange}
              placeholder="ex.: revenue, orcamentos"
            />
            <small className="clinical-field-hint">
              Identificador técnico usado internamente — sem espaços/maiúsculas/acentos,
              normalizado automaticamente enquanto você digita.
            </small>
          </label>
          <label className="form-full">
            Nome exibido (label)
            <input
              type="text"
              name="label"
              value={catalogForm.label}
              onChange={handleCatalogChange}
              placeholder="ex.: Receita, Orçamentos novos"
            />
          </label>
          <div className="form-full clinical-checkbox-group">
            <label className="clinical-checkbox-label">
              <input
                type="checkbox"
                name="applies_to_goals"
                checked={catalogForm.applies_to_goals}
                onChange={handleCatalogChange}
              />
              Usar como tipo de meta
            </label>
            <label className="clinical-checkbox-label">
              <input
                type="checkbox"
                name="applies_to_counters"
                checked={catalogForm.applies_to_counters}
                onChange={handleCatalogChange}
              />
              Usar como tipo de contador
            </label>
            <label className="clinical-checkbox-label">
              <input
                type="checkbox"
                name="active"
                checked={catalogForm.active}
                onChange={handleCatalogChange}
              />
              Ativo
            </label>
          </div>
          <div className="clinical-form-actions">
            <button className="btn-primary" onClick={handleCatalogSubmit} disabled={!companyId || !catalogFormValid}>
              {editingTypeId ? 'Atualizar tipo' : 'Criar tipo'}
            </button>
            {editingTypeId && (
              <button className="btn-secondary" onClick={handleCatalogCancelEdit}>Cancelar</button>
            )}
          </div>
        </div>

        {goalTypesLoaded && goalTypeCatalog.length === 0 ? (
          <p className="clinical-empty">
            Nenhum tipo de meta/contador cadastrado para esta unidade ainda. Cadastre o primeiro tipo acima.
          </p>
        ) : (
          <table className="clinical-table">
            <thead>
              <tr>
                <th>Key</th><th>Nome</th><th>Meta</th><th>Contador</th><th>Ativo</th><th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {goalTypeCatalog.map((t) => (
                <tr key={t.id} className={t.active ? '' : 'clinical-row-inactive'}>
                  <td><code>{t.key}</code></td>
                  <td>{t.label}</td>
                  <td>{t.applies_to_goals ? 'Sim' : 'Não'}</td>
                  <td>{t.applies_to_counters ? 'Sim' : 'Não'}</td>
                  <td>{t.active ? 'Sim' : 'Não'}</td>
                  <td>
                    <button className="btn-edit" onClick={() => handleCatalogEdit(t)}>
                      {t.active ? 'Editar' : 'Reativar/Editar'}
                    </button>{' '}
                    {t.active && (
                      <button className="btn-danger" onClick={() => handleCatalogDelete(t)}>Desativar/Remover</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </details>

      {revenueCheck && (
        <div className={`clinical-goal-check ${revenueCheck.ok ? 'goal-check-ok' : 'goal-check-erro'}`}>
          Receita: soma semanal {fmtBRL(revenueCheck.weeklySum)} / meta mensal {fmtBRL(revenueCheck.monthlyTarget)}
          {revenueCheck.ok ? ' — consistente.' : ' — DIVERGENTE (ajuste as metas).'}
        </div>
      )}

      <h2 className="clinical-section-title">{editingGoalId ? 'Editar meta' : 'Nova meta'}</h2>
      {goalTypesLoaded && goalTypeOptions.length === 0 ? (
        <p className="clinical-empty">
          Nenhum tipo de meta cadastrado para esta unidade ainda. Cadastre um tipo antes de criar metas.
        </p>
      ) : (
        <div className="clinical-form">
          <label>
            Tipo
            <select name="goal_type" value={goalForm.goal_type} onChange={handleGoalChange}>
              {goalTypeOptions.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </label>
          <label>
            Período
            <select name="period_type" value={goalForm.period_type} onChange={handleGoalChange}>
              <option value="weekly">Semanal</option>
              <option value="monthly">Mensal</option>
            </select>
          </label>
          {goalForm.period_type === 'weekly' && (
            <label>
              Semana
              <select name="week_number" value={goalForm.week_number} onChange={handleGoalChange}>
                {[1, 2, 3, 4, 5].map((w) => <option key={w} value={w}>Semana {w}</option>)}
              </select>
            </label>
          )}
          <label>
            Valor da meta
            <input type="number" min="0" step="0.01" name="target_value" value={goalForm.target_value} onChange={handleGoalChange} />
          </label>
          <div className="clinical-form-actions">
            <button
              className="btn-primary"
              onClick={handleGoalSubmit}
              disabled={!companyId || goalForm.target_value === '' || goalTypeOptions.length === 0}
            >
              {editingGoalId ? 'Atualizar meta' : 'Criar meta'}
            </button>
            {editingGoalId && (
              <button className="btn-secondary" onClick={() => { setEditingGoalId(null); setGoalForm({ ...EMPTY_GOAL, goal_type: goalTypeOptions[0]?.key || '' }); }}>
                Cancelar
              </button>
            )}
          </div>
        </div>
      )}

      {goals.length > 0 && (
        <table className="clinical-table">
          <thead>
            <tr>
              <th>Tipo</th><th>Período</th><th>Semana</th>
              <th className="num">Meta</th><th>Ativa</th><th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {goals.map((g) => (
              <tr key={g.id}>
                <td>{goalTypeLabel(goalTypeCatalog, g.goal_type)}</td>
                <td>{g.period_type === 'weekly' ? 'Semanal' : 'Mensal'}</td>
                <td>{g.week_number || '—'}</td>
                <td className="num">{g.goal_type === 'revenue' ? fmtBRL(g.target_value) : Number(g.target_value)}</td>
                <td>{g.active ? 'Sim' : 'Não'}</td>
                <td>
                  <button className="btn-edit" onClick={() => handleGoalEdit(g)}>Editar</button>{' '}
                  {g.active && <button className="btn-danger" onClick={() => handleGoalDelete(g)}>Desativar</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2 className="clinical-section-title">{editingCounterId ? 'Editar contador' : 'Novo contador de produção'}</h2>
      {goalTypesLoaded && counterTypeOptions.length === 0 ? (
        <p className="clinical-empty">
          Nenhum tipo de contador cadastrado para esta unidade ainda. Cadastre um tipo antes de lançar contadores.
        </p>
      ) : (
        <div className="clinical-form">
          <label>
            Tipo
            <select name="counter_type" value={counterForm.counter_type} onChange={handleCounterChange}>
              {counterTypeOptions.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </label>
          <label>
            Semana
            <select name="week_number" value={counterForm.week_number} onChange={handleCounterChange}>
              {[1, 2, 3, 4, 5].map((w) => <option key={w} value={w}>Semana {w}</option>)}
            </select>
          </label>
          <label>
            Quantidade
            <input type="number" min="0" step="1" name="quantity" value={counterForm.quantity} onChange={handleCounterChange} />
          </label>
          <div className="clinical-form-actions">
            <button
              className="btn-primary"
              onClick={handleCounterSubmit}
              disabled={!companyId || counterForm.quantity === '' || counterTypeOptions.length === 0}
            >
              {editingCounterId ? 'Atualizar contador' : 'Criar contador'}
            </button>
            {editingCounterId && (
              <button className="btn-secondary" onClick={() => { setEditingCounterId(null); setCounterForm({ ...EMPTY_COUNTER, counter_type: counterTypeOptions[0]?.key || '' }); }}>
                Cancelar
              </button>
            )}
          </div>
        </div>
      )}

      {counters.length > 0 && (
        <table className="clinical-table">
          <thead>
            <tr>
              <th>Tipo</th><th>Semana</th><th className="num">Quantidade</th><th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {counters.map((c) => (
              <tr key={c.id}>
                <td>{goalTypeLabel(goalTypeCatalog, c.counter_type)}</td>
                <td>{c.week_number}</td>
                <td className="num">{c.quantity}</td>
                <td>
                  <button className="btn-edit" onClick={() => handleCounterEdit(c)}>Editar</button>{' '}
                  <button className="btn-danger" onClick={() => handleCounterDelete(c)}>Excluir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default ClinicalGoalsPage;
